import {
  Announcements,
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useMinimumLoading } from '@/hooks/useMinimumLoading';
import { ReorderProps } from '@/modules/rules/hooks/useRules';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Rule } from '@/shared/types/rules';

import { RulesSkeleton } from './RulesSkeleton';
import { RuleTile } from './RuleTile/RuleTile';

interface RulesListProps {
  rules: Rule[] | undefined;
  totalRulesCount: number;
  reorderRule: (params: ReorderProps) => void;
  isReordering: boolean;
  isLoading: boolean;
  isFiltering: boolean;
}

export const RulesList = ({
  rules,
  totalRulesCount,
  reorderRule,
  isReordering,
  isLoading,
  isFiltering,
}: RulesListProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const showReorderingSkeleton = useMinimumLoading(isReordering, 300);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const getRule = (id: UniqueIdentifier) => rules?.find((r) => r.rule_id === id);
  const getPosition = (id: UniqueIdentifier) =>
    (rules?.findIndex((r) => r.rule_id === id) ?? -1) + 1;

  const announcements: Announcements = {
    onDragStart: ({ active }) => {
      const rule = getRule(active.id);
      return t('rules.a11y.drag_start', {
        0: rule?.name,
        1: getPosition(active.id),
        2: rules?.length,
      });
    },
    onDragOver: ({ active, over }) => {
      if (!over) {
        return '';
      }
      const rule = getRule(active.id);
      return t('rules.a11y.drag_over', { 0: rule?.name, 1: getPosition(over.id) });
    },
    onDragEnd: ({ active, over }) => {
      const rule = getRule(active.id);
      if (!over) {
        return t('rules.a11y.drag_cancelled', { 0: rule?.name });
      }
      return t('rules.a11y.drag_end', { 0: rule?.name, 1: getPosition(over.id) });
    },
    onDragCancel: ({ active }) => {
      const rule = getRule(active.id);
      return t('rules.a11y.drag_cancelled', { 0: rule?.name });
    },
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && rules) {
      const newIndex = rules.findIndex((r) => r.rule_id === over.id);
      const newPosition = newIndex + 1;

      reorderRule({ rule_id: active.id as string, position: newPosition });
    }
  };

  if (isLoading || showReorderingSkeleton) {
    return <RulesSkeleton count={rules?.length} />;
  }

  if (isFiltering && rules?.length === 0) {
    return (
      <p className="px-6 py-10 text-center text-sm leading-5 font-medium text-foreground-muted">
        {t('rules.no_rule_found')}
      </p>
    );
  }

  const ruleIds = rules?.map((rule) => rule.rule_id) ?? [];

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToParentElement]}
      onDragEnd={handleDragEnd}
      accessibility={{
        announcements,
        screenReaderInstructions: { draggable: t('rules.a11y.instructions') },
      }}
    >
      <SortableContext items={ruleIds} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col items-center gap-3 self-stretch pt-1">
          {rules?.map((rule) => (
            <RuleTile
              key={rule.rule_id}
              rule={rule}
              slug={slug}
              reorderRule={reorderRule}
              reorderIsLoading={isReordering}
              totalRules={totalRulesCount}
              isDragDisabled={isFiltering}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
